import {AxiosResponse} from 'axios'

import {convertListToCommaSeparatedString, unwrapAPIError} from 'utils'
import {getFlightdeckBaseUrl, requestHandler} from '../HTTP'
import {HttpMethods, EndPoints} from '../HTTP/HTTP.types'
import {
  CASBAdminConsentData,
  CASBApplicationsData,
  CASBFileLiveInfo,
  CASBFilesAPIParams,
  CASBFilesLiveInfoRequest,
  CASBFilesResponse,
  CASBOverviewData,
  CASBReviewFileInfoResponse,
  GenericMessageResponse,
  GoogleAdminConfirmRequest,
  UnshareFilesReqData,
  ReviewFilesRequestData,
  CASBShareStatsResponse,
  CASBFileLocationDetailResponse,
} from 'interfaces'
import Logger from 'services/Logger'

const flightDeckBaseUrl = getFlightdeckBaseUrl()
const CASBNeuralBaseUrl = `${flightDeckBaseUrl}${EndPoints.CASBNeuralDLP}`

const getCASBApps = async (): Promise<CASBApplicationsData> => {
  try {
    const response = await requestHandler<CASBApplicationsData>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}${EndPoints.CASBApps}`,
    })
    return response.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

// todo: Add type for response
const getCASBAdminConsent = async (data: {
  platform: string
  CASBConsentURL: string
}): Promise<AxiosResponse<CASBAdminConsentData>> => {
  try {
    const res = await requestHandler<CASBAdminConsentData>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBAdminConsent}${data.CASBConsentURL}`,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBOverview = async (data: {
  platform?: string
}): Promise<CASBOverviewData> => {
  try {
    const response = await requestHandler<CASBOverviewData>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBOverview}`,
    })

    return response.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBFiles = async (data: {
  platform?: string
  params?: CASBFilesAPIParams
}): Promise<AxiosResponse<CASBFilesResponse>> => {
  try {
    return await requestHandler<CASBFilesResponse>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBFiles}`,
      options: {
        params: {
          ...data.params,
          ...(data.params?.classifications && {
            classifications: convertListToCommaSeparatedString(
              data.params.classifications,
            ),
          }),
          ...(data.params?.shareStatus && {
            shareStatus: convertListToCommaSeparatedString(
              data.params.shareStatus,
            ),
          }),
          ...(data.params?.sharedWithDomains && {
            sharedWithDomains: convertListToCommaSeparatedString(
              data.params.sharedWithDomains,
            ),
          }),
          ...(data.params?.notSharedWithDomains && {
            notSharedWithDomains: convertListToCommaSeparatedString(
              data.params.notSharedWithDomains,
            ),
          }),
        },
      },
    })
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBReviewFileInfo = async (data: {
  platform: string
  id: string
}): Promise<AxiosResponse<CASBReviewFileInfoResponse>> => {
  try {
    const res = await requestHandler<CASBReviewFileInfoResponse>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBFiles}/${data.id}`,
    })
    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBFileLiveInfo = async (
  data: CASBFilesLiveInfoRequest,
): Promise<CASBFileLiveInfo> => {
  try {
    const res = await requestHandler({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBFiles}/${data.fileId}/live`,
    })
    return res?.data as CASBFileLiveInfo
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBFileLocationDetail = async (data: {
  appName: string
  uniqueId: string
}): Promise<CASBFileLocationDetailResponse> => {
  try {
    const res = await requestHandler<CASBFileLocationDetailResponse>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.appName}${EndPoints.CASBFiles}/${data.uniqueId}/location`,
    })
    return res.data
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const getCASBAdminConfirm = async (
  data: GoogleAdminConfirmRequest,
): Promise<AxiosResponse<GenericMessageResponse>> => {
  try {
    const res = await requestHandler<GenericMessageResponse>({
      method: HttpMethods.POST,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBAdminConfirm}`,
      data: {
        data: {
          adminEmail: data.adminEmail,
          state: data.state,
        },
      },
    })

    return res
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const unshareFiles = async (
  payload: UnshareFilesReqData,
): Promise<GenericMessageResponse> => {
  try {
    const res = await requestHandler({
      method: HttpMethods.POST,
      url: `${CASBNeuralBaseUrl}/${payload.appName}${EndPoints.CASBUnshare}`,
      data: {
        data: {
          ...payload,
        },
      },
    })

    return res.data as GenericMessageResponse
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

const putReviewFiles = async (
  payload: ReviewFilesRequestData,
): Promise<GenericMessageResponse> => {
  try {
    const res = await requestHandler({
      method: HttpMethods.PUT,
      url: `${CASBNeuralBaseUrl}/${payload.appName}${EndPoints.CASBReviewFiles}`,
      data: {
        data: {
          ...payload,
        },
      },
    })
    return res.data as GenericMessageResponse
  } catch (error) {
    const errorValue = unwrapAPIError(error)
    return Promise.reject(errorValue)
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getCASBShareStats = async (data: {
  platform: string
}): Promise<CASBShareStatsResponse> => {
  try {
    const response = await requestHandler<CASBShareStatsResponse>({
      method: HttpMethods.GET,
      url: `${CASBNeuralBaseUrl}/${data.platform}${EndPoints.CASBShareStats}`,
    })
    return response.data
  } catch (error: unknown) {
    Logger.LogError(error as Error)
    return Promise.reject(new Error(error as string))
  }
}

export const CASBService = {
  getCASBApps,
  getCASBAdminConsent,
  getCASBOverview,
  getCASBFiles,
  getCASBReviewFileInfo,
  getCASBFileLiveInfo,
  getCASBFileLocationDetail,
  getCASBAdminConfirm,
  unshareFiles,
  putReviewFiles,
  getCASBShareStats,
}
